/**
 * Confirmer — la question avant un geste qu'on ne reprend pas (lot 3 du catalogue, 28-10-2026).
 *
 * À la place de `window.confirm` (la boîte du navigateur, hors thème) et des confirmations
 * écrites à la main : une petite Modale (`sm`, `alertdialog`, au-dessus de tout), le titre en
 * Almarena, le texte gros et lisible, deux boutons en verre — « Annuler » en gris, puis
 * l'action : neutre, ou ROUGE quand elle est `danger` (supprimer, révoquer, purger).
 *
 * Le focus se pose sur l'action, donc Entrée confirme ; quand elle est dangereuse, il se pose
 * sur « Annuler » : confirmer demande un geste. Échap et le fond annulent. `enCours` gèle tout
 * pendant que ça s'exécute.
 *
 *   <Confirmer ouvert={demande} titre="Supprimer 12 comptes ?" texte="Leurs proxys sont rendus." danger
 *              confirmer="Supprimer" onConfirmer={supprimer} onAnnuler={() => setDemande(false)} />
 *
 * Ou, sans tenir d'état, comme on appelait `window.confirm` :
 *
 *   const { confirmer, dialogue } = useConfirmer();
 *   …  if (!(await confirmer({ titre: "Supprimer 12 comptes ?", danger: true }))) return;
 *   …  {dialogue}   ← posé une fois dans le rendu
 */
import { useCallback, useState, type ReactNode } from "react";

import { Button } from "./Button";
import { useLibelles } from "./libelles";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "./Modal";
import "./ouverture.css";

export interface ConfirmerProps {
  ouvert: boolean;
  /** La question, courte : « Supprimer 12 comptes ? ». */
  titre: string;
  /** Ce que ça fait, en une phrase ou deux. */
  texte?: ReactNode;
  /** L'étiquette de l'action. Défaut : « Confirmer » des libellés de la librairie (`<Libelles>` pour traduire). */
  confirmer?: string;
  /** L'étiquette du refus. Défaut : « Annuler », même source. */
  annuler?: string;
  /** L'action est irréversible : son bouton est rouge, le focus va sur « Annuler ». */
  danger?: boolean;
  /** Ça s'exécute : les boutons et la fermeture sont gelés. */
  enCours?: boolean;
  onConfirmer: () => void;
  onAnnuler: () => void;
  /** L'étiquette (lecteur d'écran) de la croix. Défaut « Fermer ». */
  closeLabel?: string;
  /** Posée dans la page — le catalogue. */
  statique?: boolean;
  className?: string;
}

export function Confirmer({
  ouvert,
  titre,
  texte,
  confirmer: confirmerDonne,
  annuler: annulerDonne,
  danger = false,
  enCours = false,
  onConfirmer,
  onAnnuler,
  closeLabel,
  statique,
  className,
}: ConfirmerProps) {
  const l = useLibelles();
  const confirmer = confirmerDonne ?? l.confirmer;
  const annuler = annulerDonne ?? l.annuler;
  return (
    <Modal
      isOpen={ouvert}
      onClose={onAnnuler}
      size="sm"
      role="alertdialog"
      ariaLabel={titre}
      zIndex={500}
      dismissOnBackdropClick={!enCours}
      dismissOnEscape={!enCours}
      statique={statique}
      className={className}
    >
      <ModalHeader title={titre} onClose={onAnnuler} closeDisabled={enCours} closeLabel={closeLabel} />
      {texte !== undefined && texte !== null && <ModalBody className="ov-texte">{texte}</ModalBody>}
      <ModalFooter>
        <Button variant="secondary" size="lg" onClick={onAnnuler} disabled={enCours} autoFocus={danger && !statique}>
          {annuler}
        </Button>
        <Button
          variant={danger ? "destructive" : "primary"}
          size="lg"
          onClick={onConfirmer}
          disabled={enCours}
          autoFocus={!danger && !statique}
        >
          {confirmer}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export type OptionsConfirmer = Omit<ConfirmerProps, "ouvert" | "onConfirmer" | "onAnnuler" | "statique" | "enCours">;

/**
 * `confirmer(options)` rend une promesse : `true` si l'action est choisie, `false` sinon (le refus,
 * Échap, le fond, la croix). Une question à la fois ; `dialogue` se pose une fois dans le rendu.
 */
export function useConfirmer(): {
  confirmer: (options: OptionsConfirmer) => Promise<boolean>;
  dialogue: ReactNode;
} {
  const [attente, setAttente] = useState<{ options: OptionsConfirmer; repondre: (ok: boolean) => void } | null>(null);

  const confirmer = useCallback(
    (options: OptionsConfirmer) =>
      new Promise<boolean>((resolve) => {
        setAttente({ options, repondre: resolve });
      }),
    [],
  );

  const repondre = (ok: boolean) => {
    attente?.repondre(ok);
    setAttente(null);
  };

  const dialogue = attente ? (
    <Confirmer ouvert {...attente.options} onConfirmer={() => repondre(true)} onAnnuler={() => repondre(false)} />
  ) : null;

  return { confirmer, dialogue };
}
